/** 模型体特征预览的展示模式。 */
export const ModelBodyPreviewPresentationMode = {
  /** 预览网格替换目标体外观，目标体进入可拓扑选择的替换预览态。 */
  Replacement: 'replacement',
  /** 预览网格仅表达刀具体等工具几何，目标体保持正式显示。 */
  ToolOverlay: 'tool-overlay',
} as const

/** 模型体特征预览展示模式类型。 */
export type ModelBodyPreviewPresentationMode =
  typeof ModelBodyPreviewPresentationMode[keyof typeof ModelBodyPreviewPresentationMode]

/** 模型体预览展示 metadata 字段名。 */
const MODEL_BODY_PREVIEW_PRESENTATION_METADATA_KEY = 'modelBodyPreviewPresentationMode'

/**
 * 为预览网格写入统一模型体展示模式。
 *
 * @param meshData 预览网格数据。
 * @param mode 展示模式。
 */
export function setModelBodyPreviewPresentationMode(
  meshData: MeshData,
  mode: ModelBodyPreviewPresentationMode,
): void {
  meshData.metadata = {
    ...(meshData.metadata ?? {}),
    [MODEL_BODY_PREVIEW_PRESENTATION_METADATA_KEY]: mode,
  }
}

/**
 * 解析预览网格需要进入替换态的模型体修订。
 *
 * 设计说明：
 * 1. 未声明模式的既有圆角、倒角和生成类特征保持 replacement 默认语义；
 * 2. tool-overlay 只改变目标体展示状态，不创建第二种预览 Mesh 或材质；
 * 3. 修订列表仍使用 Worker 输出的 modelBodyRevisionId，禁止在 Viewer 内按特征种类推断。
 *
 * @param meshData 预览网格数据。
 * @returns 去重后的上游模型体修订 ID；工具叠加模式返回空数组。
 */
export function resolveModelBodyPreviewReplacementRevisionIds(meshData: MeshData): string[] {
  if (
    meshData.metadata?.[MODEL_BODY_PREVIEW_PRESENTATION_METADATA_KEY]
    === ModelBodyPreviewPresentationMode.ToolOverlay
  ) {
    return []
  }

  const sourceRevisionIds = meshData.metadata?.previewSourceModelBodyRevisionIds
  if (!Array.isArray(sourceRevisionIds)) {
    return []
  }
  return [...new Set(sourceRevisionIds
    .filter((revisionId): revisionId is string => typeof revisionId === 'string')
    .map((revisionId) => revisionId.trim())
    .filter((revisionId) => revisionId.length > 0))]
}
